const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const MAX_SCORE = 30;
const APPLICATION_TIME_ZONE = 'America/Belem';
const CALCULATOR_ANSWER_FIELDS = [
  'mode',
  'education',
  'ageConfirm',
  'blockScores',
  'checked'
];

function datePart(value) {
  return typeof value === 'string' ? value.slice(0, 10) : '';
}

function compareDateValues(left, right) {
  return String(left ?? '').localeCompare(String(right ?? ''));
}

function orderedAssessments(assessments = []) {
  return assessments
    .map((assessment, index) => ({ assessment, index }))
    .sort((left, right) => {
      const applied = compareDateValues(left.assessment.applied_at, right.assessment.applied_at);
      if (applied !== 0) return applied;
      const created = compareDateValues(left.assessment.created_at, right.assessment.created_at);
      return created !== 0 ? created : left.index - right.index;
    })
    .map(({ assessment }) => assessment);
}

function scoreOf(assessment) {
  const score = Number(assessment?.score);
  return Number.isFinite(score) ? Math.trunc(score) : null;
}

function validateDateOnly(value) {
  if (typeof value !== 'string' || !DATE_ONLY.test(value)) {
    throw new Error('Informe uma data válida no formato AAAA-MM-DD.');
  }

  const date = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) {
    throw new Error('Informe uma data válida no formato AAAA-MM-DD.');
  }

  return value;
}

function todayDateOnly() {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: APPLICATION_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
      .formatToParts(new Date())
      .filter(({ type }) => type !== 'literal')
      .map(({ type, value }) => [type, value])
  );

  return `${parts.year}-${parts.month}-${parts.day}`;
}

function calculatorAnswersFrom(input) {
  return Object.fromEntries(
    CALCULATOR_ANSWER_FIELDS
      .filter((field) => input[field] !== undefined)
      .map((field) => [field, input[field]])
  );
}

function answersObject(value) {
  let parsed = value;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return { existing_answers_json: parsed };
    }
  }

  if (parsed === null || parsed === undefined) return {};
  if (typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
  return { existing_answers_json: parsed };
}

function normalizeAnswersJson(input) {
  const calculatorAnswers = calculatorAnswersFrom(input);
  const hasCalculatorAnswers = Object.keys(calculatorAnswers).length > 0;
  const currentAnswers = input.answers_json ?? null;

  if (!hasCalculatorAnswers) {
    return currentAnswers !== null && typeof currentAnswers !== 'string'
      ? JSON.stringify(currentAnswers)
      : currentAnswers;
  }

  return JSON.stringify({
    ...answersObject(currentAnswers),
    ...calculatorAnswers
  });
}

export function filterAssessments(assessments = [], filters = {}) {
  const { from, to, reference_key: referenceKey } = filters;
  return orderedAssessments(assessments).filter((assessment) => {
    const appliedAt = datePart(assessment.applied_at);
    return (!from || appliedAt >= from)
      && (!to || appliedAt <= to)
      && (!referenceKey || assessment.reference_key === referenceKey);
  });
}

export function calculateTrend(assessments = []) {
  const ordered = filterAssessments(assessments);
  if (ordered.length < 2) {
    return { direction: 'insufficient', label: 'dados insuficientes', delta: null };
  }

  const first = scoreOf(ordered[0]);
  const last = scoreOf(ordered[ordered.length - 1]);
  if (first === null || last === null) {
    return { direction: 'insufficient', label: 'dados insuficientes', delta: null };
  }

  const delta = last - first;
  if (delta === 0) return { direction: 'stable', label: 'estável', delta };
  if (delta > 0) return { direction: 'up', label: 'subindo', delta };
  return { direction: 'down', label: 'descendo', delta };
}

export function calculateAssessmentStats(assessments = []) {
  const scores = filterAssessments(assessments)
    .map(scoreOf)
    .filter((score) => score !== null);

  if (scores.length === 0) {
    return {
      count: 0,
      average: null,
      median: null,
      minimum: null,
      maximum: null,
      trend: calculateTrend([])
    };
  }

  const sortedScores = [...scores].sort((left, right) => left - right);
  const middle = Math.floor(sortedScores.length / 2);
  const median = sortedScores.length % 2
    ? sortedScores[middle]
    : (sortedScores[middle - 1] + sortedScores[middle]) / 2;

  return {
    count: scores.length,
    average: scores.reduce((sum, score) => sum + score, 0) / scores.length,
    median,
    minimum: sortedScores[0],
    maximum: sortedScores[sortedScores.length - 1],
    trend: calculateTrend(assessments)
  };
}

export function normalizeAssessmentPayload(input = {}) {
  const rawScore = input.score ?? input.total;
  const score = Number(rawScore);
  if (!Number.isFinite(score) || !Number.isInteger(score)) {
    throw new Error('Informe uma pontuação inteira entre 0 e 30.');
  }
  if (score < 0 || score > MAX_SCORE) {
    throw new Error('Informe uma pontuação entre 0 e 30.');
  }

  const appliedAt = validateDateOnly(input.applied_at);
  if (appliedAt > todayDateOnly()) {
    throw new Error('A data de aplicação não pode ser futura.');
  }

  return {
    score,
    applied_at: appliedAt,
    instrument_version: input.instrument_version ?? null,
    reference_key: input.reference_key ?? input.reference ?? null,
    observation: input.observation ?? null,
    answers_json: normalizeAnswersJson(input)
  };
}
